import { useState } from "react";
import { useCopyToClipboard } from "react-use";
import { CheckIcon, CopyIcon } from "lucide-react";
import { apiMutate } from "~/utils/apiClient";
import { Button } from "./button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./dialog";
import useToast from "./toast";

interface PresignedUrlItem {
  key: string;
  url: string;
  expires_at: string;
}

interface ShareLinksManifestProps {
  fileKey: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EXAMPLE_SCRIPT = `#!/bin/bash
# Run from the same directory as share-links.json. Requires: curl, jq.
count=$(jq length share-links.json)
for i in $(seq 0 $((count - 1))); do
  url=$(jq -r ".[$i].url" share-links.json)
  key=$(jq -r ".[$i].key" share-links.json)
  filename=$(basename "$key")
  echo "Downloading $filename..."
  curl -fLo "$filename" "$url"
done
echo "Done."`;

const ShareLinksManifest = ({ fileKey, open, onOpenChange }: ShareLinksManifestProps) => {
  const { showError } = useToast();
  const [isGenerating, setIsGenerating] = useState(false);
  const [, copy] = useCopyToClipboard();
  const [scriptCopied, setScriptCopied] = useState(false);

  const handleCopyScript = () => {
    copy(EXAMPLE_SCRIPT);
    setScriptCopied(true);
    setTimeout(() => setScriptCopied(false), 2000);
  };

  const handleDownload = async () => {
    if (isGenerating) return;
    setIsGenerating(true);
    const { VITE_RWB_API } = import.meta.env;
    try {
      const items = await apiMutate<PresignedUrlItem[]>(
        `${VITE_RWB_API}/file-browser/generate-download-urls`,
        "POST",
        { keys: [fileKey] }
      );
      const blob = new Blob([JSON.stringify(items, null, 2)], { type: "application/json" });
      const blobUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = blobUrl;
      anchor.style.display = "none";
      anchor.download = "share-links.json";
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(blobUrl);
      onOpenChange(false);
    } catch {
      showError("Unable to generate share links");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-base">Generate share links</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 text-sm">
          <p className="text-muted-foreground">
            You are generating a JSON file with pre-signed URLs that should only be shared with a
            trusted collaborator. Run the script below within one hour in your preferred destination
            to download the files.
          </p>
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium">Example download script</span>
            <div className="relative">
              <pre className="bg-muted rounded-md p-3 pr-10 text-xs font-mono overflow-x-auto">
                {EXAMPLE_SCRIPT}
              </pre>
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-2 right-2 size-7"
                onClick={handleCopyScript}
                aria-label="Copy script"
              >
                {scriptCopied ? (
                  <CheckIcon className="size-3.5" />
                ) : (
                  <CopyIcon className="size-3.5" />
                )}
              </Button>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleDownload} disabled={isGenerating}>
              {isGenerating ? "Generating…" : "Download share-links.json"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export { ShareLinksManifest, type ShareLinksManifestProps };
